import {computed, effectScope, shallowRef, watch} from 'vue';
import {UserIdentityTypeEnum} from '@navbar-api';
import {useUserStore} from '@navbar-logged/features/user';
import type {NavigationTabsRecord} from './navigation.types';
import {useClientNavigationTabs} from './useClientNavigationTabs';
import {useFreelancerNavigationTabs} from './useFreelancerNavigationTabs';
import {useSubcontractorNavigationTabs} from './useSubcontractorNavigationTabs';

export function useNavigationTabs(): NavigationTabsRecord {
    const userStore = useUserStore();

    const isFreelanceIdentity = computed(() => {
        return userStore.hasSelectedFreelanceIdentity || userStore.cachedIdentityRole === UserIdentityTypeEnum.Freelance;
    });

    const isSubcontractorIdentity = computed(() => {
        return userStore.hasSelectedSubcontractorIdentity || userStore.cachedIdentityRole === UserIdentityTypeEnum.Subcontractor;
    });

    const tabsRecord = shallowRef<NavigationTabsRecord>({} as NavigationTabsRecord);
    const tabsScope = effectScope();

    function runEffect() {
        if (isFreelanceIdentity.value) {
            tabsRecord.value = tabsScope.run(() => {
                return useFreelancerNavigationTabs();
            })!;
        } else if (isSubcontractorIdentity.value) {
            tabsRecord.value = tabsScope.run(() => {
                return useSubcontractorNavigationTabs();
            })!;
        } else {
            tabsRecord.value = tabsScope.run(() => {
                return useClientNavigationTabs();
            })!;
        }
    }

    watch(isFreelanceIdentity, runEffect);
    watch(isSubcontractorIdentity, runEffect);
    runEffect();

    const allNavigationRoutes = computed(() => tabsRecord.value?.allNavigationRoutes.value);
    const filteredNavigationRoutes = computed(() => tabsRecord.value?.filteredNavigationRoutes.value);

    const logoNavigationRoute = computed(() => tabsRecord.value?.logoNavigationRoute.value);

    return {
        logoNavigationRoute,
        allNavigationRoutes,
        filteredNavigationRoutes,
    };
}
